// Frontend/geniac/src/Components/Settings/PlanComparisonModal.tsx
import React, { useState } from 'react';
import { FiX, FiCheck, FiX as FiXMark, FiAlertCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface PlanComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlan: string;
  targetPlan: string;
  isLoading?: boolean;
}

const PlanComparisonModal: React.FC<PlanComparisonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentPlan,
  targetPlan,
  isLoading = false
}) => {
  const { t } = useTranslation('settings');
  const [confirmed, setConfirmed] = useState(false);

  if (!isOpen) return null;

  // Plan features configuration
  const planFeatures = {
    free: {
      name: 'Gratuit',
      price: '0€',
      features: [
        { name: 'Projets', value: '3', included: true },
        { name: 'Déploiements par mois', value: '5', included: true },
        { name: 'Utilisateurs', value: '1', included: true },
        { name: 'Templates de base', value: 'Limités', included: true },
        { name: 'Support', value: 'Communauté', included: true },
        { name: 'Multi-cloud', value: false, included: false },
        { name: 'FinOps avancé', value: false, included: false },
        { name: 'Monitoring temps réel', value: false, included: false },
        { name: 'White label', value: false, included: false },
        { name: 'Support prioritaire', value: false, included: false }
      ]
    },
    standard: {
      name: 'Standard',
      price: '49€',
      features: [
        { name: 'Projets', value: '20', included: true },
        { name: 'Déploiements par mois', value: '50', included: true },
        { name: 'Utilisateurs', value: '5', included: true },
        { name: 'Templates complets', value: 'Tous', included: true },
        { name: 'Support', value: 'Email', included: true },
        { name: 'Multi-cloud', value: true, included: true },
        { name: 'FinOps de base', value: true, included: true },
        { name: 'Monitoring de base', value: true, included: true },
        { name: 'White label', value: false, included: false },
        { name: 'Support prioritaire', value: false, included: false }
      ]
    },
    premium: {
      name: 'Premium',
      price: '149€',
      features: [
        { name: 'Projets', value: 'Illimités', included: true },
        { name: 'Déploiements par mois', value: 'Illimités', included: true },
        { name: 'Utilisateurs', value: 'Illimités', included: true },
        { name: 'Templates complets', value: 'Tous', included: true },
        { name: 'Support', value: 'Email + Chat', included: true },
        { name: 'Multi-cloud', value: true, included: true },
        { name: 'FinOps avancé', value: true, included: true },
        { name: 'Monitoring temps réel', value: true, included: true },
        { name: 'White label', value: true, included: true },
        { name: 'Support prioritaire', value: true, included: true }
      ]
    }
  };

  const current = planFeatures[currentPlan.toLowerCase() as keyof typeof planFeatures] || planFeatures.premium;
  const target = planFeatures[targetPlan.toLowerCase() as keyof typeof planFeatures] || planFeatures.standard;

  const handleConfirm = () => {
    if (confirmed) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="h-6 w-6" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-center mb-3">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <FiAlertCircle className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Comparaison des plans
            </h3>
            <p className="text-sm text-gray-600 text-center">
              Voici ce qui changera si vous passez au plan {target.name}
            </p>
          </div>

          {/* Comparison table */}
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fonctionnalité
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {current.name}
                    <div className="text-2xl font-bold text-gray-900 mt-1">{current.price}</div>
                    <div className="text-xs font-normal text-gray-500 lowercase">par mois</div>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {target.name}
                    <div className="text-2xl font-bold text-gray-900 mt-1">{target.price}</div>
                    <div className="text-xs font-normal text-gray-500 lowercase">par mois</div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {current.features.map((feature, index) => {
                  const targetFeature = target.features[index];
                  const willLose = feature.included && !targetFeature.included;
                  const willDecrease =
                    feature.included &&
                    targetFeature.included &&
                    typeof feature.value === 'string' &&
                    typeof targetFeature.value === 'string' &&
                    feature.value !== targetFeature.value &&
                    (feature.value.includes('Illimité') || parseInt(feature.value) > parseInt(targetFeature.value));

                  return (
                    <tr
                      key={index}
                      className={willLose || willDecrease ? 'bg-red-50' : ''}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {feature.name}
                        {(willLose || willDecrease) && (
                          <span className="ml-2 text-red-600 text-xs">
                            (Perte)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        {typeof feature.value === 'boolean' ? (
                          feature.value ? (
                            <FiCheck className="h-5 w-5 text-green-600 mx-auto" />
                          ) : (
                            <FiXMark className="h-5 w-5 text-gray-300 mx-auto" />
                          )
                        ) : (
                          <span className="text-gray-900 font-medium">{feature.value}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        {typeof targetFeature.value === 'boolean' ? (
                          targetFeature.value ? (
                            <FiCheck className="h-5 w-5 text-green-600 mx-auto" />
                          ) : (
                            <FiXMark className="h-5 w-5 text-gray-300 mx-auto" />
                          )
                        ) : (
                          <span className={`font-medium ${willLose || willDecrease ? 'text-red-600' : 'text-gray-900'}`}>
                            {targetFeature.value}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Warning message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <FiAlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800 mb-1">
                  Attention
                </h4>
                <p className="text-sm text-yellow-700">
                  Le changement sera effectif à la fin de votre période de facturation actuelle.
                  Vous conservez tous vos avantages actuels jusqu'à cette date.
                  Les fonctionnalités marquées comme "Perte" ne seront plus disponibles après le changement.
                </p>
              </div>
            </div>
          </div>

          {/* Confirmation checkbox */}
          <div className="mb-6">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <span className="ml-3 text-sm text-gray-700">
                Je comprends que je vais perdre certaines fonctionnalités et je souhaite continuer avec le changement vers le plan {target.name}.
              </span>
            </label>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={!confirmed || isLoading}
              className="w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-3 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Traitement...' : `Confirmer le changement vers ${target.name}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanComparisonModal;
